import {
  Archive as ArchiveIcon,
  Campaign as CampaignIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Unarchive as UnarchiveIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Breadcrumb } from 'src/components/Breadcrumb';
import { HoverUnderline } from './HoverUnderline.styles';
import { StatusChip } from 'src/UIKit';
import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import GridData from 'src/components/GridData/GridData';
import { TableHeader, TABLE_HEADER_ACTION_SX, TABLE_HEADER_ACTIONS_WRAP_SX } from 'src/components/TableHeader/TableHeader';
import { MountingComponent } from 'src/components/MountingComponent';
import PrivateLayoutView from 'src/Layout/PrivateLayout/PrivateLayoutView';
import { getDateTimeUSAFormat } from 'src/constants/helpers';
import { campaignBuilderPath, toCampaignEdit } from 'src/modules/campaigns/helpers';
import {
  CreateContactModal,
  DownloadCsvModal,
  ContactsView,
  AddContactsToGroupDialog,
} from 'src/modules/contacts/components';
import {
  useCreateContactModalController,
  useDownloadCsvModalController,
  useAddContactsToGroupDialogController,
} from 'src/modules/contacts/controllers';
import { useGroupsViewController, useDynamicGroupRulesFormDialogController } from '../../controllers';
import { GroupFormDialog } from '../GroupForm';
import { useSelector } from 'react-redux';
import { DynamicGroupRulesFormDialog } from '../DynamicGroupRulesForm';
import { buildFormData } from '../../helpers';
import { getCampaignConfigs } from 'src/modules/campaignConfigBuilder/slices';
import toast from 'react-hot-toast';

export const GroupsView = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const { state } = useLocation();

  const {
    navigate,
    isPageLoading,
    groupsFilter,
    groupList,
    reloadGroups,
    changeGroupFilterForm,
    changeGroupsSearchFilter,
    changeGroupsSorting,
    changeGroupsPagination,
    archiveGroup,
    unarchiveGroup,
    groupFormDialogController,
  } = useGroupsViewController();

  const { openDialog, ConfirmationDialog } = useConfirmationDialog();

  const createContactModalController = useCreateContactModalController();
  const downloadCsvModalController = useDownloadCsvModalController();
  const addContactsToGroupDialogController = useAddContactsToGroupDialogController();
  const dynamicGroupRulesFormDialogController = useDynamicGroupRulesFormDialogController();

  const campaignConfigs = useSelector(getCampaignConfigs);

  const availableForContactGroupsConfig = React.useMemo(
    () => campaignConfigs.find(item => item.config.availableForContactGroups),
    [campaignConfigs],
  );

  const handleAddContacts = React.useCallback(() => {
    const formGroupId = groupFormDialogController.initFormData?.id;
    const formGroupName = groupFormDialogController.initFormData?.name;
    if (formGroupId) {
      addContactsToGroupDialogController.open(formGroupId, formGroupName);
    }
  }, [
    groupFormDialogController.initFormData?.id,
    groupFormDialogController.initFormData?.name,
    addContactsToGroupDialogController,
  ]);

  const handleEditRules = React.useCallback(() => {
    const formGroupId = groupFormDialogController.initFormData?.id;
    const selectedGroup = groupList?.items?.find(g => g.id === formGroupId);
    if (selectedGroup) {
      dynamicGroupRulesFormDialogController.open(selectedGroup);
    }
  }, [
    groupFormDialogController.initFormData?.id,
    groupList?.items,
    dynamicGroupRulesFormDialogController,
  ]);

  const handleCreateBulkCampaign = React.useCallback(
    (groupId: string) => {
      if (!availableForContactGroupsConfig?.id) {
        toast.error('Campaign config not found');
        return;
      }

      navigate(campaignBuilderPath(`?configId=${availableForContactGroupsConfig.id}`), {
        state: { preselectedGroupId: groupId },
      });
    },
    [availableForContactGroupsConfig?.id, navigate],
  );

  const displayGroups = React.useMemo(() => {
    const items = groupList?.items || [];
    return [
      ...items,
      {
        id: 'spam',
        name: 'Spam',
        type: 'default' as const,
        status: 'active' as const,
        validContactCount: 0,
        invalidContactCount: 0,
        rules: [],
        joinCampaigns: [],
        createdAt: '',
        updatedAt: '',
        description: '',
      },
      {
        id: 'invalid-contacts',
        name: 'Invalid Contacts',
        type: 'default' as const,
        status: 'active' as const,
        validContactCount: 0,
        invalidContactCount: 0,
        rules: [],
        joinCampaigns: [],
        createdAt: '',
        updatedAt: '',
        description: '',
      },
    ];
  }, [groupList?.items]);

  const memoizedHeader = React.useMemo(() => {
    if (!groupId) {
      return <Breadcrumb items={[{ label: 'Contact Groups', isActive: true }]} />;
    }

    const selectedGroup = groupList?.items?.find(g => g.id === groupId);
    const groupName = selectedGroup?.name || 'Contact Groups';
    const isDefaultGroup = selectedGroup?.type === 'default';

    return (
      <Breadcrumb
        items={[
          {
            label: 'Contact Groups',
            path: '/groups',
          },
          {
            label: isDefaultGroup ? 'All Contacts' : groupName,
            isActive: true,
          },
        ]}
      />
    );
  }, [groupId, groupList?.items]);

  return (
    <>
      <GroupFormDialog
        {...groupFormDialogController}
        onAddContacts={handleAddContacts}
        onEditRules={handleEditRules}
      />

      <AddContactsToGroupDialog {...addContactsToGroupDialogController} onSuccess={reloadGroups} />
      <DynamicGroupRulesFormDialog
        {...dynamicGroupRulesFormDialogController}
        submit={async (full, dirty, nextAction) => {
          const updatedGroup = await dynamicGroupRulesFormDialogController.submit(
            full,
            dirty,
            nextAction,
          );
          if (updatedGroup) {
            groupFormDialogController.syncRules(buildFormData(updatedGroup).rules || []);
          }

          return updatedGroup;
        }}
        onSuccess={reloadGroups}
      />

      <MountingComponent conditions={[createContactModalController.show]}>
        <CreateContactModal {...createContactModalController} refresh={reloadGroups} />
      </MountingComponent>

      <MountingComponent conditions={[downloadCsvModalController.show]}>
        <DownloadCsvModal {...downloadCsvModalController} refreshGroups={reloadGroups} />
      </MountingComponent>

      <MountingComponent conditions={[groupFormDialogController.isOpen]}>
        <GroupFormDialog
          {...groupFormDialogController}
          onAddContacts={handleAddContacts}
          onEditRules={handleEditRules}
        />
      </MountingComponent>

      <ConfirmationDialog />

      <>
        <PrivateLayoutView title={memoizedHeader}>
          {!groupId ? (
            <GridData
              header={
                <TableHeader
                  searchValue={groupsFilter.query}
                  onSearchChange={changeGroupsSearchFilter}
                  actions={
                    <Box sx={TABLE_HEADER_ACTIONS_WRAP_SX}>
                      <Button
                        onClick={() => createContactModalController.open()}
                        variant="contained"
                        sx={TABLE_HEADER_ACTION_SX}
                      >
                        Add Contacts
                      </Button>

                      <Button
                        onClick={downloadCsvModalController.open}
                        variant="contained"
                        sx={TABLE_HEADER_ACTION_SX}
                      >
                        Import Contacts
                      </Button>

                      <Button
                        onClick={() => groupFormDialogController.open()}
                        variant="contained"
                        sx={TABLE_HEADER_ACTION_SX}
                      >
                        Create Group
                      </Button>
                    </Box>
                  }
                />
              }
              isLoading={isPageLoading}
              rows={displayGroups}
              columns={[
                {
                  field: 'name',
                  headerName: 'Group',
                  renderCell: r => {
                    return (
                      <>
                        {r.row.name} {r.row.status === 'archived' ? <b>(Archive)</b> : ''}
                      </>
                    );
                  },
                  minWidth: 200,
                  editable: false,
                  sortable: true,
                  flex: 1,
                },
                {
                  field: 'type',
                  headerName: 'Type',
                  renderCell: r => {
                    if (r.row.type == 'default') {
                      return (
                        <StatusChip label="Default" />
                      );
                    }

                    if (r.row.type == 'custom') {
                      return (
                        <StatusChip label="Standard" tone="brandLight" />
                      );
                    }

                    if (r.row.type == 'dynamic') {
                      return (
                        <StatusChip label="Dynamic" tone="brandDark" />
                      );
                    }
                  },
                  minWidth: 100,
                  editable: false,
                  sortable: true,
                  flex: 1,
                },
                {
                  field: 'validContactCount',
                  headerName: 'Contacts',
                  renderCell: r => {
                    const isSpamOrInvalidContactsGroup = r.row.id === 'spam' || r.row.id === 'invalid-contacts';
                    if (isSpamOrInvalidContactsGroup) return '-';
                    const totalContacts =
                      Number(r.row.validContactCount || 0) + Number(r.row.invalidContactCount || 0);

                    return (
                      <Stack direction="row" alignItems="center" height="100%">
                        {totalContacts.toLocaleString('en-US')}
                      </Stack>
                    );
                  },
                  minWidth: 180,
                  editable: false,
                  sortable: true,
                  flex: 1,
                },
                {
                  field: 'createdAt',
                  headerName: 'Created',
                  renderCell: r => {
                    if (!r.row.createdAt) return '-';
                    return getDateTimeUSAFormat(new Date(r.row.createdAt));
                  },
                  minWidth: 100,
                  editable: false,
                  sortable: true,
                  flex: 1,
                },
                {
                  field: 'updatedAt',
                  headerName: 'Updated',
                  renderCell: r => {
                    if (!r.row.updatedAt) return '-';
                    return getDateTimeUSAFormat(new Date(r.row.updatedAt));
                  },
                  minWidth: 100,
                  editable: false,
                  sortable: true,
                  flex: 1,
                },
                {
                  field: 'joinCampaigns',
                  headerName: 'Join Campaigns',
                  renderCell: r => {
                    const quantity = r.row.joinCampaigns?.length;

                    if (!quantity) {
                      return null;
                    }

                    return (
                      <Tooltip
                        title={
                          <Grid container spacing={1} direction="column">
                            {r.row.joinCampaigns.map((c, i) => (
                              <Grid item key={i}>
                                <HoverUnderline
                                  onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toCampaignEdit(navigate, c);
                                  }}
                                >
                                  {c.name}
                                </HoverUnderline>
                              </Grid>
                            ))}
                          </Grid>
                        }
                        arrow
                        placement="left"
                      >
                        <Grid container spacing={1}>
                          <Grid item>{quantity}</Grid>
                          <Grid item display="flex" alignItems="center" justifyContent="center">
                            <PersonAddIcon sx={{ scale: 0.8 }} />
                          </Grid>
                        </Grid>
                      </Tooltip>
                    );
                  },
                  minWidth: 100,
                  editable: false,
                  sortable: false,
                  flex: 1,
                },
              ]}
              onRowClick={r => {
                if (r.id === 'spam') {
                  navigate('/group/spam');
                  return;
                }
                if (r.id === 'invalid-contacts') {
                  navigate('/group/invalid-contacts');
                  return;
                }
                navigate(`/group/${r.id}`);
              }}
              renderAction={r => {
                if (r.type === 'default' || r.id === 'spam' || r.id === 'invalid-contacts') {
                  return null;
                }

                return (
                  <>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => groupFormDialogController.open(r)}
                    >
                      <EditIcon fontSize="inherit" />
                    </IconButton>

                    {availableForContactGroupsConfig && (
                      <Tooltip title="Create bulk campaign">
                        <IconButton
                          size="small"
                          color="primary"
                          aria-label={`Create bulk campaign for ${r.name}`}
                          onClick={event => {
                            event.stopPropagation();
                            handleCreateBulkCampaign(r.id);
                          }}
                        >
                          <CampaignIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    )}

                    {r.status === 'active' && (
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() =>
                          openDialog({
                            title: `Archive "${r.name}"?`,
                            content:
                              'By archiving this group it will be removed from all campaigns that it is currently associated with. Are you sure you want to archive this group?',
                            onConfirm: () => archiveGroup(r.id),
                          })
                        }
                      >
                        <ArchiveIcon fontSize="inherit" />
                      </IconButton>
                    )}

                    {r.status === 'archived' && (
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() =>
                          openDialog({
                            title: `Unarchive "${r.name}"?`,
                            content: 'Are you sure you want to unarchive this group?',
                            onConfirm: () => unarchiveGroup(r.id),
                          })
                        }
                      >
                        <UnarchiveIcon fontSize="inherit" />
                      </IconButton>
                    )}
                  </>
                );
              }}
              sorting={{
                value: groupsFilter.sort,
                onChange: changeGroupsSorting,
              }}
              pagination={{
                page: groupsFilter.page,
                pageSize: groupsFilter.limit,
                totalCount: groupList?.count,
                onChange: changeGroupsPagination,
              }}
              paginationLeftPart={
                <Grid container spacing={1} alignItems="center" px={1}>
                  <Grid item>
                    <Typography variant="caption">Archived Groups:</Typography>
                  </Grid>

                  <Grid item>
                    <ToggleButtonGroup
                      value={groupsFilter.statuses}
                      onChange={(_, v) => v && changeGroupFilterForm({ statuses: v })}
                      size="small"
                      color="primary"
                      exclusive
                    >
                      <ToggleButton value={'active,archived'}>Show</ToggleButton>
                      <ToggleButton value={'active'}>Hide</ToggleButton>
                    </ToggleButtonGroup>
                  </Grid>

                </Grid>
              }
            />
          ) : (
            <ContactsView
              groupId={groupId}
              embedded
              isLoading={false}
              openDynamicRules={state?.openDynamicRules}
            />
          )}
        </PrivateLayoutView>
      </>
    </>
  );
};
